import { parse } from 'csv-parse/sync';
import { detect } from 'csv-string';
import { Comma } from 'csv-string/dist/types';

import { CsvJsonOption, CsvOptionRow, TrueFalseValue } from './model';
import { ImportExportLevel } from './public_model';

const pushToAcc = (acc: CsvJsonOption[], record: CsvOptionRow, parentLevel: number, currentIndex: number) => {
    const filledLevel = determineFilledLevel(record, parentLevel) as keyof CsvOptionRow;
    const depth = determineDepth(record, parentLevel);
    let currentDepthAcc = acc;
    for (let index = 0; index < depth; index++) {
        // @ts-ignore
        currentDepthAcc =
            currentDepthAcc[currentDepthAcc.length - 1] && currentDepthAcc[currentDepthAcc.length - 1].options ?
                currentDepthAcc[currentDepthAcc.length - 1].options
            :   (currentDepthAcc[currentDepthAcc.length - 1].options = []);
    }

    currentDepthAcc.push({
        optionName: record[filledLevel] as string,
        levelName: record['Level name'] || '',
        levelDescription: record['Level description'] || '',
        isEnabled: (record['Enabled'] as TrueFalseValue) || 'true',
        isDefault: (record['Default'] as TrueFalseValue) || 'false',
        id: record['ID option'] || '',
        options: [],
        rowNumber: currentIndex + 1,
    });

    return depth;
};

const levels = ['Level 1', 'Level 2', 'Level 3', 'Level 4', 'Level 5', 'Level 6', 'Level 7'];

const getHeaders = (value: string, delimiter: string) => {
    const headers = value.split('\n')[0].replace('\r', '');
    return headers.split(delimiter);
};

export const csvToJson = (value: string) => {
    const delimiter = detect(value) as Comma;
    const headers = getHeaders(value, delimiter);
    const records = parse(value, {
        delimiter,
        trim: true,
        skip_empty_lines: true,
        columns: true,
    }) as CsvOptionRow[];
    let parentLevel = -1;
    const csvJsonOptions = records.reduce((acc, record, currentIndex) => {
        parentLevel = pushToAcc(acc, record, parentLevel, currentIndex);
        return acc;
    }, []) as CsvJsonOption[];

    return {
        headers,
        rows: records,
        options: csvJsonOptions,
    };
};

export const mapToImportExportLevel = (csvJsonOptions: CsvJsonOption[]): ImportExportLevel => {
    const firstItem = csvJsonOptions[0];

    return {
        description: firstItem.levelDescription,
        levelName: firstItem.levelName,
        options: csvJsonOptions.map(item => ({
            value: item.optionName,
            isEnabled: item.isEnabled,
            isDefault: item.isDefault,
            id: item.id,
            nestedLevel: item.options?.length ? mapToImportExportLevel(item.options) : undefined,
        })),
    };
};

const levelNameToDepth = {
    'Level 1': 0,
    'Level 2': 1,
    'Level 3': 2,
    'Level 4': 3,
    'Level 5': 4,
    'Level 6': 5,
    'Level 7': 6,
} as Record<string, number>;

const determineDepth = (record: CsvOptionRow, parentLevel: number) => {
    const filledLevelName = determineFilledLevel(record, parentLevel);
    return levelNameToDepth[filledLevelName];
};

const determineFilledLevel = (record: CsvOptionRow, parentLevel: number) => {
    const levelKeys = Object.entries(record)
        .filter(([key, value]) => levels.includes(key) && !!value)
        .map(([key]) => key);
    const depth = levelKeys[0] ? levelNameToDepth[levelKeys[0]] : (parentLevel + 1) % 7;
    return depth < parentLevel + 1 ? levelKeys[0] : levels[(parentLevel + 1) % 7];
};
